import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Button, Input, Label, EmptyState } from "../components/ui-kit";
import { Plus, Trash2, CalendarClock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { thisYearIST } from "../lib/datefmt";

const MANAGE_ROLES = ["admin", "manager", "director"];

export default function HolidaysPage() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(thisYearIST());
  const initial = { id: null, date: "", name: "", is_optional: false };
  const [form, setForm] = useState(initial);
  const editing = !!form.id;

  const load = async () => {
    try {
      const r = await api.get(`/holidays?year=${year}`);
      setRows(r.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) return toast.error("Date and name are required");
    const payload = { date: form.date, name: form.name.trim(), is_optional: !!form.is_optional };
    try {
      if (editing) {
        await api.put(`/holidays/${form.id}`, payload);
        toast.success("Holiday updated");
      } else {
        await api.post("/holidays", payload);
        toast.success("Holiday added");
      }
      setForm(initial);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const edit = (h) => setForm({ id: h.id, date: h.date, name: h.name, is_optional: !!h.is_optional });
  const del = async (h) => {
    if (!window.confirm(`Delete holiday "${h.name}" on ${h.date}?`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const m = r.date.slice(0, 7);
      if (!map.has(m)) map.set(m, []);
      map.get(m).push(r);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Public Holidays"
        subtitle="National, regional and optional off-days used by the Attendance grid"
        actions={
          <div className="flex items-center gap-2">
            <Label className="mb-0">Year</Label>
            <Input
              type="number"
              className="w-24"
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || year)}
              data-testid="holidays-year"
            />
          </div>
        }
      />

      {canManage && (
        <Card className="mb-6">
          <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <Plus className="w-4 h-4" /> {editing ? "Edit holiday" : "Add holiday"}
          </h3>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="holiday-date"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Diwali, Republic Day"
                data-testid="holiday-name"
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <input
                id="optional"
                type="checkbox"
                checked={form.is_optional}
                onChange={(e) => setForm({ ...form, is_optional: e.target.checked })}
                data-testid="holiday-optional"
              />
              <label htmlFor="optional" className="text-sm">Optional</label>
            </div>
            <div className="sm:col-span-4 flex justify-end gap-2">
              {editing && (
                <Button variant="outline" type="button" onClick={() => setForm(initial)}>Cancel</Button>
              )}
              <Button type="submit" data-testid="holiday-submit">{editing ? "Save changes" : "Add holiday"}</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState icon={CalendarClock} title="No holidays for this year yet" description={canManage ? "Add holidays above." : "Ask an admin to add holidays."} />
          </div>
        ) : (
          <div>
            {grouped.map(([month, items]) => (
              <div key={month} className="border-b border-[#E5E2DC] last:border-b-0">
                <div className="px-4 py-2 bg-[#F9F8F6] text-[11px] uppercase tracking-widest text-[#5C6B5E] font-bold">
                  {new Date(`${month}-01T12:00:00Z`).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", month: "long", year: "numeric" })} · {items.length}
                </div>
                <table className="min-w-full text-sm">
                  <tbody>
                    {items.map((h) => (
                      <tr key={h.id} className="border-b border-[#E5E2DC] last:border-b-0" data-testid={`holiday-row-${h.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap w-40 font-mono">
                          {h.date}
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-[#5C6B5E]">
                            {new Date(`${h.date}T12:00:00Z`).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short" })}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold">{h.name}</div>
                          {h.is_optional && <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Optional</div>}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#5C6B5E]">
                          {h.created_by_name && `Added by ${h.created_by_name}`}
                        </td>
                        {canManage && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => edit(h)} className="text-[#1E3F20] p-1 hover:bg-[#F1EFEA] rounded mr-1" data-testid={`edit-holiday-${h.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => del(h)} className="text-red-600 p-1 hover:bg-red-50 rounded" data-testid={`del-holiday-${h.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
